// Validator rules for the workshop profile (KULA-REVISE-WORKSHOP-PLAN.md §9,
// corrected by §0c: editions per case study). Pure. The run-level validator
// calls `workshopIssues` for blocks and `workshopProseFields` for the
// register rules, so a workshop is gated exactly like a Study Lab.

import type { ReviseIssue } from '../validate';
import { IB_REGIONS, WORKSHOP_PARAGRAPH_CHOICES, workshopPairKey, type WorkshopBlock, type WorkshopEdition, type WorkshopLabelKey, type WorkshopPairOverride } from '../types';
import { WORKSHOP_COPY_KEYS, WORKSHOP_QUICK_KEYS } from './copy';

const LABEL_KEYS: readonly WorkshopLabelKey[] = ['command', 'what', 'who', 'time', 'idea'];
const COMMAND_TERMS = ['discuss', 'evaluate', 'to-what-extent', 'examine', 'compare-contrast'];

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const str = (v: unknown): v is string => typeof v === 'string' && v.trim().length > 0;
const strList = (v: unknown): v is string[] => Array.isArray(v) && v.every((x) => typeof x === 'string');

/** The case-study sets a learner can end up with: the fixed set, or every single or pair the selection allows. */
export function reachableSelections(w: WorkshopBlock): string[][] {
  const ids = isObj(w.caseStudies) ? Object.keys(w.caseStudies) : [];
  if (!isObj(w.selection)) return [];
  if ('fixed' in w.selection) return Array.isArray(w.selection.fixed) ? [w.selection.fixed.filter((k) => typeof k === 'string')] : [];
  if (w.selection.pick === 1) return ids.map((k) => [k]);
  const out: string[][] = [];
  for (let i = 0; i < ids.length; i++) {
    for (let j = i + 1; j < ids.length; j++) {
      if (w.selection.regionRule) {
        const ra = w.caseStudies[ids[i]]?.region;
        const rb = w.caseStudies[ids[j]]?.region;
        if (ra && rb && ra === rb) continue;
      }
      out.push([ids[i], ids[j]]);
    }
  }
  return out;
}

/** Every block a workshop block earns. Empty means it is playable. */
export function workshopIssues(w: WorkshopBlock): ReviseIssue[] {
  const out: ReviseIssue[] = [];
  const block = (code: string, message: string, screenId?: string) => out.push({ code, severity: 'block', message, screenId });

  if (!str(w.question)) block('workshop.question', 'workshop.question is required.');
  if (!COMMAND_TERMS.includes(w.commandTerm)) block('workshop.commandTerm', `workshop.commandTerm must be one of ${COMMAND_TERMS.join(', ')}.`);
  if (typeof w.coach !== 'boolean') block('workshop.coach', 'workshop.coach must be true (with the coach) or false (without).');
  // The start screen and the band
  if (!isObj(w.brand) || !str(w.brand.line) || !str(w.brand.title) || !str(w.brand.titleAccent)) block('workshop.brand', 'workshop.brand needs line, title and titleAccent.');
  if (!str(w.exam)) block('workshop.exam', 'workshop.exam (the exam line on the start screen) is required.');
  if (!str(w.intro)) block('workshop.intro', 'workshop.intro (the start screen hook) is required.');
  if (!str(w.coachStrip)) block('workshop.coachStrip', 'workshop.coachStrip is required.');
  if (!Array.isArray(w.how) || w.how.length === 0 || !w.how.every((h) => isObj(h) && str(h.head) && str(h.text))) block('workshop.how', 'workshop.how needs at least one card with head and text.');
  if (!isObj(w.watch) || !str(w.watch.head) || !str(w.watch.text)) block('workshop.watch', 'workshop.watch needs head and text.');
  if (!isObj(w.unit) || !str(w.unit.singular) || !str(w.unit.plural)) block('workshop.unit', 'workshop.unit needs singular and plural (e.g. war / wars).');
  if (!isObj(w.themeWord) || !str(w.themeWord.singular) || !str(w.themeWord.plural)) block('workshop.themeWord', 'workshop.themeWord needs singular and plural (e.g. theme / themes).');

  // The question-parts step
  if (!Array.isArray(w.parts) || w.parts.length === 0) block('workshop.parts', 'workshop.parts must list the parts of the question.');
  if (w.labels !== undefined && !Array.isArray(w.labels)) block('workshop.labels', 'workshop.labels must be a list.');
  const labels = Array.isArray(w.labels) ? w.labels : [];
  const labelKeys = new Set(labels.filter(isObj).map((l) => l.key));
  labels.forEach((l, i) => {
    if (!isObj(l)) block('workshop.labels', `labels[${i}] is not an object.`);
    else if (!LABEL_KEYS.includes(l.key)) block('workshop.labels', `Label key "${l.key}" is not one of ${LABEL_KEYS.join(', ')}.`);
  });
  (Array.isArray(w.parts) ? w.parts : []).forEach((p, i) => {
    if (!isObj(p)) { block('workshop.parts', `parts[${i}] is not an object.`); return; }
    if (!str(p.text)) block('workshop.parts', `parts[${i}] has no text.`);
    if (!labelKeys.has(p.label)) block('workshop.parts', `parts[${i}] carries label "${p.label}", which is not in workshop.labels.`);
  });
  (Array.isArray(w.asks) ? w.asks : []).forEach((a, i) => {
    if (!isObj(a)) block('workshop.asks', `asks[${i}] is not an object.`);
    else if (!labelKeys.has(a.key)) block('workshop.asks', `asks "${a.name}" carries key "${a.key}", which is not in workshop.labels.`);
  });

  // Case studies, selection, editions
  const cases = isObj(w.caseStudies) ? Object.keys(w.caseStudies) : [];
  if (cases.length === 0) block('workshop.caseStudies', 'workshop.caseStudies must hold at least one case study.');
  const regionRule = isObj(w.selection) && !('fixed' in w.selection) && !!w.selection.regionRule;
  for (const k of cases) {
    const c = w.caseStudies[k];
    if (!isObj(c)) { block('workshop.caseStudies', `Case study "${k}" is not an object.`); continue; }
    if (!str(c.name) || !str(c.short) || !str(c.head)) block('workshop.caseStudies', `Case study "${k}" needs short, name and head.`);
    if (!strList(c.match) || c.match.length === 0) block('workshop.caseStudies', `Case study "${k}" needs match fragments so the writing checks can find it.`);
    if (c.region !== undefined && !(IB_REGIONS as readonly string[]).includes(c.region)) block('workshop.caseStudies', `Case study "${k}" region "${c.region}" is not an IB region: ${IB_REGIONS.join(', ')}.`);
    if (regionRule && !str(c.region)) block('workshop.caseStudies', `Case study "${k}" needs a region, because the selection applies the region rule.`);
    if (!isObj(w.editions) || !isObj(w.editions[k])) block('workshop.editions', `Case study "${k}" has no edition. Every case study needs one, keyed by its id.`);
  }
  const editionKeys = isObj(w.editions) ? Object.keys(w.editions) : [];
  for (const k of editionKeys) if (!cases.includes(k)) block('workshop.editions', `Edition "${k}" is not a case study. Editions are keyed by case-study id.`);

  let pick: 1 | 2 | null = null;
  if (!isObj(w.selection)) block('workshop.selection', 'workshop.selection is required ({fixed: [ids]} or {pick: 1 | 2}).');
  else if ('fixed' in w.selection) {
    const f = w.selection.fixed;
    if (!strList(f) || f.length < 1 || f.length > 2) block('workshop.selection', 'selection.fixed must name one or two case studies.');
    else {
      for (const id of f) if (!cases.includes(id)) block('workshop.selection', `selection.fixed names case study "${id}", which does not exist.`);
      if (new Set(f).size !== f.length) block('workshop.selection', 'selection.fixed names the same case study twice.');
      pick = f.length === 2 ? 2 : 1;
    }
  } else {
    if (w.selection.pick !== 1 && w.selection.pick !== 2) block('workshop.selection', 'selection.pick must be 1 or 2.');
    else {
      pick = w.selection.pick;
      if (pick === 2 && cases.length < 2) block('workshop.selection', 'selection.pick is 2 but there are fewer than two case studies.');
    }
  }
  const selections = reachableSelections(w);
  if (pick && selections.length === 0 && cases.length) block('workshop.selection', 'No choice of case studies is allowed: every pair breaks the region rule.');

  // Shared tables
  if (!Array.isArray(w.readings) || w.readings.length === 0) block('workshop.readings', 'workshop.readings needs at least one reading.');
  if (!Array.isArray(w.jobs) || w.jobs.length === 0) block('workshop.jobs', 'workshop.jobs needs the paragraph jobs.');
  if (!Number.isInteger(w.minFacts) || w.minFacts < 1) block('workshop.minFacts', 'workshop.minFacts must be a whole number of at least 1.');
  if (!isObj(w.writing) || !strList(w.writing.introMust) || !strList(w.writing.conclusionMust)) block('workshop.writing', 'workshop.writing needs introMust and conclusionMust lists.');
  if (pick === 2) {
    if (!w.scale || !strList(w.scale.labels) || !strList(w.scale.text) || w.scale.labels.length !== 3 || w.scale.text.length !== 3) {
      block('workshop.scale', 'Two case studies are compared, so workshop.scale needs exactly three labels and three text forms.');
    }
  }

  for (const key of editionKeys) if (cases.includes(key)) out.push(...editionIssues(w, key, w.editions[key], pick ?? 1));

  // Two case studies: the editions must agree on their themes, and every
  // reachable pair must be writable, by hand in `pairs` or by generation
  // from the per-theme compare lines.
  if (pick === 2) {
    const names = (k: string) => (Array.isArray(w.editions[k]?.themes) ? w.editions[k].themes.map((t) => (isObj(t) ? String(t.name) : '')) : []);
    const first = editionKeys.find((k) => cases.includes(k));
    if (first) {
      const base = names(first);
      for (const k of editionKeys) {
        if (k === first || !cases.includes(k)) continue;
        const other = names(k);
        if (other.length !== base.length || other.some((n, i) => n !== base[i])) block('workshop.themes', `Editions "${first}" and "${k}" disagree on their themes. A two-case workshop's editions carry the same themes in the same order.`);
      }
    }
    const pairs = isObj(w.pairs) ? w.pairs : {};
    for (const k of Object.keys(pairs)) {
      const parts = k.split('+');
      if (parts.length !== 2 || parts.some((p) => !cases.includes(p)) || workshopPairKey(parts[0], parts[1]) !== k) block('workshop.pairs', `pairs key "${k}" must be two case-study ids, sorted and joined with "+".`);
      else out.push(...pairIssues(w, k, pairs[k]));
    }
    for (const [a, b] of selections) {
      const pk = workshopPairKey(a, b);
      const hand = pairs[pk];
      const complete = isObj(hand) && Array.isArray(hand.topicSentences) && Array.isArray(hand.positions) && isObj(hand.hints);
      if (complete) continue;
      for (const k of [a, b]) {
        const themes = Array.isArray(w.editions[k]?.themes) ? w.editions[k].themes : [];
        themes.forEach((t, i) => {
          if (!isObj(t)) return;
          const c = t.compare;
          if (!isObj(c) || !str(c.changed) || !str(c.limited) || !str(c.reason) || !str(c.counter) || !str(c.show)) {
            block('workshop.compare', `Pair ${pk} is not hand-written in pairs, so edition "${k}" theme ${i} needs compare lines (changed, limited, reason, counter, show) to generate it.`);
          }
        });
      }
    }
  }
  return out;
}

function pairIssues(w: WorkshopBlock, key: string, p: WorkshopPairOverride): ReviseIssue[] {
  const out: ReviseIssue[] = [];
  const block = (code: string, message: string) => out.push({ code, severity: 'block', message, screenId: key });
  const [a] = key.split('+');
  const n = Array.isArray(w.editions[a]?.themes) ? w.editions[a].themes.length : 0;
  if (!isObj(p)) { block('workshop.pairs', `pairs["${key}"] is not an object.`); return out; }
  if (p.topicSentences !== undefined) checkTopicSentences(p.topicSentences, n, `pairs["${key}"]`, block);
  if (p.positions !== undefined) checkPositions(p.positions, n, `pairs["${key}"]`, block);
  if (p.hints !== undefined) checkHints(p.hints, Array.isArray(p.positions) ? p.positions.length : 0, n, true, w.scale?.labels.length, undefined, undefined, `pairs["${key}"]`, block);
  if (p.context !== undefined && !str(p.context)) block('workshop.pairs', `pairs["${key}"].context must be a sentence.`);
  return out;
}

type Block = (code: string, message: string) => void;

function checkTopicSentences(ts: unknown, n: number, where: string, block: Block) {
  if (!Array.isArray(ts) || ts.length !== n) { block('workshop.topicSentences', `${where}: topicSentences must have one entry per theme (${n}).`); return; }
  ts.forEach((list, i) => {
    if (!Array.isArray(list)) { block('workshop.topicSentences', `${where}: topicSentences[${i}] must be a list.`); return; }
    if (list.length === 0) block('workshop.topicSentences', `${where} theme ${i} has no topic sentences.`);
    for (const t of list) if (!isObj(t) || !str(t.text) || !str(t.need)) block('workshop.topicSentences', `${where} theme ${i}: every topic sentence needs text and need.`);
  });
}

function checkPositions(positions: unknown, n: number, where: string, block: Block) {
  const idx = (v: unknown) => Number.isInteger(v) && (v as number) >= 0 && (v as number) < n;
  if (!Array.isArray(positions) || positions.length === 0) { block('workshop.positions', `${where} has no positions.`); return; }
  positions.forEach((p, i) => {
    if (!isObj(p)) { block('workshop.positions', `${where} position ${i} is not an object.`); return; }
    if (!str(p.title) || !str(p.detail) || !str(p.build)) block('workshop.positions', `${where} position ${i} needs title, detail and build.`);
    if (!Array.isArray(p.reasons) || p.reasons.length === 0) block('workshop.positions', `${where} position ${i} has no reasons.`);
    if (!Array.isArray(p.counters) || p.counters.length === 0) block('workshop.positions', `${where} position ${i} has no counters.`);
    for (const r of [...(Array.isArray(p.reasons) ? p.reasons : []), ...(Array.isArray(p.counters) ? p.counters : [])]) {
      if (!isObj(r)) { block('workshop.positions', `${where} position ${i}: every reason and counter must be an object.`); continue; }
      if (!idx(r.theme)) block('workshop.positions', `${where} position ${i}: a reason or counter names theme ${r.theme}, which is out of range.`);
      if (!str(r.text)) block('workshop.positions', `${where} position ${i}: every reason and counter needs text.`);
    }
  });
}

function checkHints(hints: unknown, positionCount: number, n: number, twoCase: boolean, scaleSize: number | undefined, counterTheme: number | undefined, anchorTheme: number | undefined, where: string, block: Block) {
  if (!isObj(hints) || !Array.isArray(hints.order) || !Number.isInteger(hints.fallback)) { block('workshop.hints', `${where} needs hints { order, fallback }.`); return; }
  const pIdx = (v: unknown) => Number.isInteger(v) && (v as number) >= 0 && (v as number) < positionCount;
  const idx = (v: unknown) => Number.isInteger(v) && (v as number) >= 0 && (v as number) < n;
  if (!pIdx(hints.fallback)) block('workshop.hints', `${where}: hints.fallback ${hints.fallback} is not a position.`);
  (hints.order as unknown[]).forEach((h, i) => {
    if (!isObj(h)) { block('workshop.hints', `${where}: hints.order[${i}] is not an object.`); return; }
    if (!pIdx(h.position)) block('workshop.hints', `${where}: hints.order[${i}] names position ${h.position}, which does not exist.`);
    for (const c of Array.isArray(h.when) ? h.when : []) {
      if (!isObj(c)) block('workshop.hints', `${where}: hints.order[${i}] has a clause that is not an object.`);
      else if ('judgement' in c) {
        if (!twoCase) block('workshop.hints', `${where}: hints.order[${i}] counts judgements, but there is one case study and no compare step.`);
        if (c.themes === 'counter' && counterTheme === undefined) block('workshop.hints', `${where}: hints.order[${i}] names the counter theme, but the edition sets none.`);
        if (scaleSize !== undefined && (typeof c.judgement !== 'number' || c.judgement < 0 || c.judgement >= scaleSize)) block('workshop.hints', `${where}: hints.order[${i}] judgement ${c.judgement} is off the scale.`);
      } else if ('rankOf' in c) {
        if (c.rankOf === 'anchor' && anchorTheme === undefined) block('workshop.hints', `${where}: hints.order[${i}] ranks the anchor theme, but the edition sets none.`);
        if (typeof c.rankOf === 'number' && !idx(c.rankOf)) block('workshop.hints', `${where}: hints.order[${i}] ranks theme ${c.rankOf}, which is out of range.`);
      } else block('workshop.hints', `${where}: hints.order[${i}] has a clause that is neither a judgement count nor a rank.`);
    }
  });
}

function editionIssues(w: WorkshopBlock, key: string, e: WorkshopEdition, pick: 1 | 2): ReviseIssue[] {
  const out: ReviseIssue[] = [];
  const where = `Edition "${key}"`;
  const block = (code: string, message: string) => out.push({ code, severity: 'block', message, screenId: key });
  if (!isObj(e)) {
    block('workshop.edition', `${where} is not an object.`);
    return out;
  }
  if (!str(e.context)) block('workshop.edition', `${where} needs a context sentence for the introduction.`);

  const themes = Array.isArray(e.themes) ? e.themes : [];
  const n = themes.length;
  const minThemes = Math.max(...WORKSHOP_PARAGRAPH_CHOICES);
  if (n < minThemes) block('workshop.themes', `${where} needs at least ${minThemes} themes, one per body paragraph when the learner chooses ${minThemes} (got ${n}).`);
  themes.forEach((t, i) => {
    if (!isObj(t)) { block('workshop.themes', `${where} theme ${i} is not an object.`); return; }
    if (!str(t.name) || !str(t.low) || !str(t.out) || !str(t.what) || !str(t.question)) block('workshop.themes', `${where} theme ${i} needs name, low, out, what and question.`);
    if (!str(t.evidence)) block('workshop.themes', `${where} theme "${t.name}" has no evidence.`);
    if (!str(t.matchLine)) block('workshop.themes', `${where} theme "${t.name}" has no matchLine.`);
  });
  const idx = (v: unknown) => Number.isInteger(v) && (v as number) >= 0 && (v as number) < n;
  if (e.counterTheme !== undefined && !idx(e.counterTheme)) block('workshop.counterTheme', `${where}: counterTheme ${e.counterTheme} is out of range.`);
  if (e.anchorTheme !== undefined && !idx(e.anchorTheme)) block('workshop.anchorTheme', `${where}: anchorTheme ${e.anchorTheme} is out of range.`);

  const rawFacts = isObj(e.facts) ? e.facts : {};
  if (Object.keys(rawFacts).length === 0) block('workshop.facts', `${where} has no facts.`);
  // Only well-formed facts take part in the cross-checks below; a malformed
  // one is reported once here and then ignored rather than thrown on.
  const facts: typeof rawFacts = {};
  for (const id of Object.keys(rawFacts)) {
    if (isObj(rawFacts[id])) facts[id] = rawFacts[id];
    else block('workshop.facts', `${where} fact "${id}" is not an object.`);
  }
  const factIds = Object.keys(facts);
  for (const id of factIds) {
    const f = facts[id];
    if (!idx(f.theme)) block('workshop.facts', `${where} fact "${id}" names theme ${f.theme}, which is out of range.`);
    if (!str(f.text)) block('workshop.facts', `${where} fact "${id}" has no text.`);
    if (!strList(f.keywords) || f.keywords.length === 0) block('workshop.facts', `${where} fact "${id}" has no keywords, so the writing check cannot find it.`);
    if (!str(f.use)) block('workshop.facts', `${where} fact "${id}" has no \`use\` phrase.`);
  }
  themes.forEach((t, i) => {
    if (isObj(t) && !factIds.some((id) => facts[id].theme === i)) block('workshop.facts', `${where} theme "${t.name}" has no fact, so no paragraph plan on it can pass.`);
  });

  const pages = Array.isArray(e.revision) ? e.revision : [];
  if (pages.length === 0) block('workshop.revision', `${where} has no revision pages.`);
  let recallScreens = 0;
  pages.forEach((p, i) => {
    if (!isObj(p)) { block('workshop.revision', `${where} revision page ${i} is not an object.`); return; }
    if (!str(p.lede)) block('workshop.revision', `${where} revision page ${i} has no lede.`);
    if (!Array.isArray(p.cards) || p.cards.length === 0) block('workshop.revision', `${where} revision page ${i} has no cards.`);
    for (const c of Array.isArray(p.cards) ? p.cards : []) if (!isObj(c) || !str(c.head) || !strList(c.points) || c.points.length === 0) block('workshop.revision', `${where} revision page ${i}: every card needs head and points.`);
    if (p.recall !== undefined && !isObj(p.recall)) block('workshop.recall', `${where} revision page ${i}: recall is not an object.`);
    else if (p.recall) {
      recallScreens++;
      if (!str(p.recall.name)) block('workshop.recall', `${where} revision page ${i}: recall needs a name.`);
      const hasFacts = strList(p.recall.facts) && p.recall.facts.length > 0;
      const hasKeys = Array.isArray(p.recall.keys) && p.recall.keys.length > 0;
      if (hasFacts === hasKeys) block('workshop.recall', `${where} revision page ${i}: recall needs either facts (ids) or keys (key facts), not both and not neither.`);
      // Every key fact goes into the flashcard deck when missed, so each needs its question.
      for (const id of p.recall.facts ?? []) {
        if (!facts[id]) block('workshop.recall', `${where} revision page ${i}: recall names fact "${id}", which does not exist.`);
        else if (!str(facts[id].cue)) block('workshop.recall', `${where} fact "${id}" is a key fact on a recall screen but has no cue (its flashcard question).`);
      }
      (Array.isArray(p.recall.keys) ? p.recall.keys : []).forEach((k, j) => {
        if (!isObj(k) || !str(k.text) || !str(k.cue)) block('workshop.recall', `${where} revision page ${i}: key fact ${j} needs text and cue (its flashcard question).`);
      });
    }
  });
  if (recallScreens === 0) block('workshop.recall', `${where} has no recall screen. Every factor's revision page is followed by one (Leah, rule 3).`);

  const hist = isObj(e.historians) ? e.historians : {};
  for (const k of Object.keys(hist)) {
    const h = hist[k];
    if (!isObj(h)) block('workshop.historians', `${where} historian "${k}" is not an object.`);
    else if (!str(h.name) || !str(h.opener) || !strList(h.match) || h.match.length === 0) block('workshop.historians', `${where} historian "${k}" needs name, opener and match fragments.`);
  }

  const perTheme = (name: string, v: unknown): unknown[][] => {
    if (!Array.isArray(v) || v.length !== n) {
      block(`workshop.${name}`, `${where}: ${name} must have one entry per theme (${n}).`);
      return [];
    }
    return v.map((list, i) => {
      if (Array.isArray(list)) return list as unknown[];
      block(`workshop.${name}`, `${where}: ${name}[${i}] must be a list.`);
      return [];
    });
  };
  perTheme('elsewhere', e.elsewhere).forEach((ids, i) => {
    for (const id of ids) {
      if (typeof id !== 'string' || !facts[id]) block('workshop.elsewhere', `${where} elsewhere[${i}] names fact "${id}", which does not exist.`);
      else if (facts[id].theme === i) block('workshop.elsewhere', `${where} elsewhere[${i}] names fact "${id}", which belongs to this very theme.`);
    }
  });
  perTheme('perspectives', e.perspectives).forEach((list, i) => {
    if (list.length === 0) block('workshop.perspectives', `${where} theme ${i} has no perspectives.`);
    for (const p of list) {
      if (!isObj(p)) { block('workshop.perspectives', `${where} theme ${i}: every perspective must be an object.`); continue; }
      if (typeof p.historian !== 'string' || !hist[p.historian]) block('workshop.perspectives', `${where} theme ${i} names historian "${p.historian}", which is not in historians.`);
      if (!str(p.line)) block('workshop.perspectives', `${where} theme ${i}: every perspective needs a line.`);
    }
  });

  // A one-case workshop writes its argument content in full on the edition;
  // a two-case one generates it (or hand-writes it in pairs).
  const oneCase = pick === 1;
  if (oneCase || e.topicSentences !== undefined) checkTopicSentences(e.topicSentences, n, where, block);
  if (oneCase || e.positions !== undefined) checkPositions(e.positions, n, where, block);
  if (oneCase || e.hints !== undefined) checkHints(e.hints, Array.isArray(e.positions) ? e.positions.length : 0, n, !oneCase, w.scale?.labels.length, e.counterTheme, e.anchorTheme, where, block);

  const ch = e.checks;
  if (!isObj(ch)) block('workshop.checks', `${where} needs checks { claim, link, themeWords }.`);
  else {
    if (!strList(ch.claim) || ch.claim.length === 0) block('workshop.checks', `${where} checks.claim is empty; the intro and conclusion checks need it.`);
    if (!strList(ch.link) || ch.link.length === 0) block('workshop.checks', `${where} checks.link is empty; the link check needs it.`);
    if (!strList(ch.themeWords) || ch.themeWords.length === 0) block('workshop.checks', `${where} checks.themeWords is empty; the outline check needs it.`);
    if (ch.yearRange !== undefined && (!Array.isArray(ch.yearRange) || ch.yearRange.length !== 2 || !Number.isInteger(ch.yearRange[0]) || !Number.isInteger(ch.yearRange[1]) || ch.yearRange[0] > ch.yearRange[1])) block('workshop.checks', `${where} checks.yearRange must be [from, to].`);
    for (const x of Array.isArray(ch.extra) ? ch.extra : []) if (!isObj(x) || !str(x.label) || !Array.isArray(x.all) || x.all.length === 0 || !str(x.yes) || !str(x.no)) block('workshop.checks', `${where}: every extra check needs label, all, yes and no.`);
  }
  for (const k of Object.keys(e.copy ?? {})) if (!WORKSHOP_COPY_KEYS.includes(k as never)) block('workshop.copy', `${where} copy key "${k}" is not one the engine reads. Known: ${WORKSHOP_COPY_KEYS.join(', ')}.`);
  for (const k of Object.keys(e.quick ?? {})) if (!(WORKSHOP_QUICK_KEYS as readonly string[]).includes(k)) block('workshop.quick', `${where} quick answers are keyed by "${k}", which is not a step or group. Known: ${WORKSHOP_QUICK_KEYS.join(', ')}.`);
  return out;
}

/** Every string a learner reads, with a path, for the register rules. */
export function workshopProseFields(w: WorkshopBlock): Array<[string, string]> {
  const out: Array<[string, string]> = [];
  const add = (path: string, v: unknown) => {
    if (typeof v === 'string' && v.trim()) out.push([path, v]);
  };
  add('question', w.question);
  add('exam', w.exam);
  add('intro', w.intro);
  (w.how ?? []).forEach((h, i) => add(`how[${i}]`, h.text));
  add('coachStrip', w.coachStrip);
  (w.asks ?? []).forEach((a, i) => add(`asks[${i}]`, a.text));
  add('watch', w.watch?.text);
  if (w.selection && !('fixed' in w.selection)) add('selection.lede', w.selection.lede);
  for (const [k, c] of Object.entries(w.caseStudies ?? {})) add(`caseStudies.${k}.about`, c?.about);
  (w.readings ?? []).forEach((r, i) => add(`readings[${i}]`, r.text));
  (w.jobs ?? []).forEach((j, i) => add(`jobs[${i}]`, j.text));
  (w.writing?.introMust ?? []).forEach((t, i) => add(`writing.introMust[${i}]`, t));
  (w.writing?.conclusionMust ?? []).forEach((t, i) => add(`writing.conclusionMust[${i}]`, t));
  (w.writing?.paragraphStarters ?? []).forEach((s, i) => add(`writing.paragraphStarters[${i}]`, s.text));
  (w.writing?.conclusionStarters ?? []).forEach((s, i) => add(`writing.conclusionStarters[${i}]`, s.text));
  const addSentences = (p: string, ts: unknown) => (Array.isArray(ts) ? ts : []).forEach((list, i) => (Array.isArray(list) ? list : []).forEach((t, j) => {
    add(`${p}.topicSentences[${i}][${j}].text`, t?.text);
    add(`${p}.topicSentences[${i}][${j}].need`, t?.need);
  }));
  const addPositions = (p: string, positions: unknown) => (Array.isArray(positions) ? positions : []).forEach((pos, i) => {
    add(`${p}.positions[${i}].title`, pos?.title);
    add(`${p}.positions[${i}].detail`, pos?.detail);
    add(`${p}.positions[${i}].build`, pos?.build);
    (pos?.reasons ?? []).forEach((r: { text?: unknown }, j: number) => add(`${p}.positions[${i}].reasons[${j}]`, r.text));
    (pos?.counters ?? []).forEach((r: { text?: unknown }, j: number) => add(`${p}.positions[${i}].counters[${j}]`, r.text));
  });
  for (const [key, e] of Object.entries(w.editions ?? {})) {
    const p = `editions.${key}`;
    add(`${p}.context`, e.context);
    (e.themes ?? []).forEach((t, i) => {
      add(`${p}.themes[${i}].what`, t.what);
      add(`${p}.themes[${i}].question`, t.question);
      add(`${p}.themes[${i}].evidence`, t.evidence);
      add(`${p}.themes[${i}].matchLine`, t.matchLine);
      for (const [k, v] of Object.entries(t.compare ?? {})) add(`${p}.themes[${i}].compare.${k}`, v);
    });
    for (const [id, f] of Object.entries(e.facts ?? {})) {
      add(`${p}.facts.${id}.text`, f.text);
      add(`${p}.facts.${id}.use`, f.use);
      add(`${p}.facts.${id}.note`, f.note);
      add(`${p}.facts.${id}.cue`, f.cue);
    }
    (e.revision ?? []).forEach((pg, i) => {
      add(`${p}.revision[${i}].lede`, pg.lede);
      (pg.cards ?? []).forEach((c, j) => (c.points ?? []).forEach((pt, k) => add(`${p}.revision[${i}].cards[${j}].points[${k}]`, pt)));
      (pg.recall?.keys ?? []).forEach((k, j) => {
        add(`${p}.revision[${i}].recall.keys[${j}].text`, k?.text);
        add(`${p}.revision[${i}].recall.keys[${j}].cue`, k?.cue);
      });
    });
    addSentences(p, e.topicSentences);
    (e.perspectives ?? []).forEach((list, i) => list.forEach((x, j) => add(`${p}.perspectives[${i}][${j}].line`, x.line)));
    addPositions(p, e.positions);
    for (const [step, list] of Object.entries(e.quick ?? {})) (list ?? []).forEach((qa, i) => {
      add(`${p}.quick.${step}[${i}].q`, qa[0]);
      add(`${p}.quick.${step}[${i}].a`, qa[1]);
    });
    add(`${p}.checks.caseMessage`, e.checks?.caseMessage);
    for (const [k, v] of Object.entries(e.copy ?? {})) add(`${p}.copy.${k}`, v);
    (e.checks?.extra ?? []).forEach((x, i) => {
      add(`${p}.checks.extra[${i}].yes`, x.yes);
      add(`${p}.checks.extra[${i}].no`, x.no);
    });
  }
  for (const [key, pr] of Object.entries(w.pairs ?? {})) {
    const p = `pairs.${key}`;
    add(`${p}.context`, pr?.context);
    addSentences(p, pr?.topicSentences);
    addPositions(p, pr?.positions);
    add(`${p}.checks.caseMessage`, pr?.checks?.caseMessage);
  }
  return out;
}
